// ---------------------------------------------------------------------------
// Shared constants – domain keywords, stop-words, campaign type taxonomy, etc.
// ---------------------------------------------------------------------------

import type { CampaignTypeConfig, SentimentStyleConfig } from './types';

// ── ML Campaign Types (single source of truth) ─────────────────────────────
// Dual SVM classifier categories used across all sections.

export const ML_CAMPAIGN_TYPES: Record<string, CampaignTypeConfig> = {
  // Direct Action Model (svm_da.pkl)
  'Direct Action':     { keywords: ['protest', 'demonstration', 'rally', 'activist', 'march', 'occupation'], color: '#9333ea' },
  'Higher Education':  { keywords: ['university', 'student', 'campus', 'education', 'college', 'academic'], color: '#3b82f6' },
  'Industrial Action': { keywords: ['strike', 'worker', 'union', 'labor', 'employee', 'walkout'], color: '#ef4444' },
  'International':     { keywords: ['global', 'international', 'foreign', 'embassy', 'diplomat', 'border'], color: '#10b981' },
  'Pro-Environmental': { keywords: ['environment', 'climate', 'pollution', 'green', 'sustainability', 'carbon'], color: '#22c55e' },
  // Animal Rights Model (svm_AR.pkl)
  'Animal Rights':     { keywords: ['animal rights', 'vegan', 'cruelty', 'abuse', 'liberation'], color: '#8b5cf6' },
  'Animal Welfare':    { keywords: ['animal welfare', 'pet', 'shelter', 'rescue', 'protection'], color: '#ec4899' },
  'Anti-Captivity':    { keywords: ['zoo', 'captivity', 'cage', 'circus', 'aquarium'], color: '#f59e0b' },
  'Anti-GM':           { keywords: ['gmo', 'genetically modified', 'genetic engineering', 'monsanto'], color: '#eab308' },
  'Anti-Government':   { keywords: ['government', 'regime', 'authoritarian', 'election', 'corruption', 'coup'], color: '#dc2626' },
  'Anti-Vivisection':  { keywords: ['vivisection', 'animal testing', 'lab', 'experiment', 'research'], color: '#7c3aed' },
};

// ── Sentiment display config ────────────────────────────────────────────────

export const SENTIMENT_STYLES: Record<string, SentimentStyleConfig> = {
  positive: { label: 'Positive', color: '#48bb78' },
  negative: { label: 'Negative', color: '#f56565' },
  neutral:  { label: 'Neutral',  color: '#a0aec0' },
  mixed:    { label: 'Mixed',    color: '#ed8936' },
};

// ── Sentiment analysis dictionaries ─────────────────────────────────────────

export const POSITIVE_PATTERNS: Record<string, number> = {
  'peace agreement': 5, 'ceasefire': 5, 'peaceful resolution': 5,
  'humanitarian aid': 5, 'rescue operation': 5, 'evacuation': 5,
  'diplomatic breakthrough': 5, 'peaceful protest': 5,
  'peace': 4, 'victory': 4, 'success': 4, 'liberation': 4, 'freedom': 4,
  'rescued': 4, 'saved': 4, 'recovery': 4, 'rebuilt': 4, 'restored': 4,
  'agreement': 3, 'progress': 3, 'improved': 3, 'resolved': 3, 'stable': 3,
  'cooperation': 3, 'alliance': 3, 'unity': 3, 'support': 3, 'aid': 3,
  'help': 3, 'assistance': 3, 'relief': 3, 'calm': 3, 'normal': 3,
  'hope': 2, 'optimistic': 2, 'positive': 2, 'better': 2, 'improving': 2,
  'decrease': 2, 'reduced': 2, 'declining': 2, 'dialogue': 2, 'talks': 2,
};

export const NEGATIVE_PATTERNS: Record<string, number> = {
  'mass killing': 6, 'war crime': 6, 'genocide': 6, 'ethnic cleansing': 6,
  'terrorist attack': 6, 'suicide bombing': 6, 'massacre': 6,
  'killed': 5, 'execution': 5, 'slaughter': 5, 'assassination': 5,
  'terrorism': 5, 'terrorist': 5,
  'death': 4, 'deaths': 4, 'attack': 4, 'attacked': 4, 'violence': 4,
  'war': 4, 'conflict': 4, 'crisis': 4, 'casualties': 4, 'wounded': 4,
  'injured': 4, 'hostage': 4, 'kidnapped': 4, 'torture': 4, 'destroyed': 4,
  'bombing': 4, 'explosion': 4, 'strike': 4, 'missile': 4, 'rocket': 4,
  'shelling': 4, 'airstrike': 4, 'bombardment': 4,
  'threat': 3, 'danger': 3, 'damage': 3, 'devastation': 3, 'displacement': 3,
  'refugees': 3, 'fled': 3, 'displaced': 3, 'protest': 3, 'riot': 3,
  'clashes': 3, 'fighting': 3, 'combat': 3, 'tension': 3, 'escalation': 3,
  'sanctions': 3, 'embargo': 3, 'blockade': 3, 'siege': 3, 'occupation': 3,
  'unrest': 3, 'chaos': 3, 'turmoil': 3,
  'concern': 2, 'fears': 2, 'warning': 2, 'risk': 2, 'instability': 2,
  'dispute': 2, 'accusation': 2, 'allegations': 2, 'arrested': 2,
  'charged': 2, 'investigation': 2, 'scandal': 2,
};

export const NEUTRAL_PATTERNS: Record<string, number> = {
  'reported': -1, 'announced': -1, 'confirmed': -1, 'stated': -1,
  'according': -1, 'sources': -1, 'officials': -1, 'meeting': -1,
  'discussed': -1, 'conference': -1, 'statement': -1,
};

// ── Keyword extraction data ─────────────────────────────────────────────────

export const DOMAIN_KEYWORDS: Set<string> = new Set([
  'protest','protests','protester','protesters','protesting',
  'demonstration','demonstrations','demonstrator','demonstrators',
  'rally','rallies','march','marches','marching',
  'activism','activist','activists','campaign','campaigns',
  'boycott','boycotts','strike','strikes','blockade','blockades',
  'occupation','occupying','sit-in','vigil','vigils',
  'animal','animals','wildlife','species','habitat','conservation',
  'cruelty','abuse','welfare','rights','liberation','vegan','veganism',
  'slaughter','slaughterhouse','factory','farm','farming','farms',
  'hunting','poaching','trafficking','endangered','extinction',
  'laboratory','testing','experiments','vivisection','research',
  'fur','leather','ivory','captivity','zoo','zoos','circus',
  'sanctuary','rescue','shelter','adoption','rehoming',
  'threat','threats','attack','attacks','violence','violent',
  'extremist','extremism','radical','terrorism','terrorist',
  'arson','vandalism','sabotage','harassment','intimidation',
  'infiltration','surveillance','investigation','arrest','arrested',
  'criminal','illegal','prosecution','convicted','sentenced',
  'peta','hsus','aspca','aldf','greenpeace','wwf',
  'release','released','freed','rescue','rescued','liberated',
  'exposed','expose','exposing','undercover',
  'petition','petitions','lawsuit','legal','court','trial',
  'university','college','campus','laboratory','facility',
  'corporation','company','industry','pharmaceutical','cosmetics',
  'airport','transport','shipping','cargo',
  'climate','environmental','pollution','deforestation','ecosystem',
  'police','authorities','government','legislation','law','ban',
  'regulation','policy','reform','advocacy','awareness',
]);

export const STOP_WORDS: Set<string> = new Set([
  'the','a','an','and','or','but','in','on','at','to','for','of',
  'with','by','as','is','are','was','were','be','been','being',
  'have','has','had','do','does','did','will','would','could','should',
  'shall','may','might','must','can','need','dare','ought',
  'i','me','my','mine','we','us','our','ours','you','your','yours',
  'he','him','his','she','her','hers','it','its','they','them','their',
  'theirs','this','that','these','those','who','whom','whose','which',
  'what','where','when','why','how','all','each','every','both',
  'few','more','most','other','some','such','no','nor','not','only',
  'own','same','so','than','too','very','just','also',
  'from','into','through','during','before','after','above','below',
  'between','under','again','further','then','once','here','there',
  'about','against','among','around','because','since','until','while',
  'january','february','march','april','june','july','august',
  'september','october','november','december',
  'monday','tuesday','wednesday','thursday','friday','saturday','sunday',
  'today','yesterday','tomorrow','week','month','year','day','days',
  'time','times','hour','hours','minute','minutes','morning','evening',
  'announced','reported','said','told','according','news','report',
  'reports','source','sources','officials','official','spokesperson',
  'stated','confirmed','claimed','alleged','allegedly','reportedly',
  'forbes','reuters','associated','press','bbc','cnn','guardian',
  'nytimes','washingtonpost','bloomberg','politico','huffpost',
  'outside','inside','international','national','local','global',
  'people','person','group','groups','members','member','community',
  'however','although','therefore','meanwhile','furthermore','moreover',
  'including','following','regarding','concerning','whether',
  'going','coming','getting','making','taking','saying','doing','having',
  'using','looking','showing','working','trying','finding','giving',
  'being','seeing','knowing','thinking','wanting',
  'thing','things','way','ways','place','places','part','parts',
  'case','cases','point','points','fact','facts','area','areas',
  'number','numbers','world','country','state','city','region',
  'new','old','good','bad','great','little','big','small','large',
  'high','low','long','short','first','last','next','many','much',
  'several','different','same','other','another','certain','possible',
  'http','https','www','com','org','net','html','amp','quot',
  'via','pic','twitter','facebook','instagram','youtube',
]);

export const COMMON_BIGRAMS: Set<string> = new Set([
  'th','he','in','er','an','re','on','at','en','nd','ti','es',
  'or','te','of','ed','is','it','al','ar','st','to','nt','ng',
  'se','ha','as','ou','io','le','ve','co','me','de','hi','ri',
  'ro','ic','ne','ea','ra','ce','li','ch','ll','be','ma','si',
  'om','ur','ca','el','ta','la','ns','di','fo','ho','pe','ec',
  'pr','no','ct','us','ac','ot','il','tr','ly','nc','et','ut',
  'ss','so','rs','un','lo','wa','ge','ie','wh','ee','wi','em',
  'ad','ol','rt','po','we','na','ul','ni','ts','mo','ow','pa',
  'im','pl','ay','ub','go','sa','iv','fi','su','ag','bl',
]);

// ── Risk-level keywords ─────────────────────────────────────────────────────

export const HIGH_RISK_WORDS  = ['killed', 'death', 'attack', 'bombing', 'massacre', 'strike', 'war', 'crisis'];
export const MED_RISK_WORDS   = ['injured', 'wounded', 'damage', 'conflict', 'violence', 'clash', 'protest'];
export const LOW_RISK_WORDS   = ['reported', 'announced', 'meeting'];

// ── Source icon map ─────────────────────────────────────────────────────────

export const SOURCE_ICON_MAP: Record<string, string> = {
  twitter: 'twitter',
  facebook: 'facebook',
  instagram: 'instagram',
  reddit: 'reddit',
  news: 'newspaper',
  blog: 'blog',
};

